import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";


const api = new API();


/*
|--------------------------------------------------------------------------
| DATE HELPER
|--------------------------------------------------------------------------
*/

const formatDateForInput = (
    date = new Date()
) => {

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth() + 1
        ).padStart(
            2,
            "0"
        );

    const day =
        String(
            date.getDate()
        ).padStart(
            2,
            "0"
        );


    return `${year}-${month}-${day}`;
};


const getTodayDate = () => {

    return formatDateForInput(
        new Date()
    );
};


const todayDate =
    getTodayDate();


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {

    data:
        null,

    loading:
        false,

    refreshing:
        false,

    error:
        null,

    /*
    |--------------------------------------------------------------------------
    | GLOBAL DASHBOARD FILTER
    |--------------------------------------------------------------------------
    |
    | We use CUSTOM because frontend always sends exact dates.
    |
    | Quick buttons like Today / Week / Month simply calculate
    | the dates and send them using CUSTOM.
    |
    |--------------------------------------------------------------------------
    */

    filters: {

        period:
            "TODAY",

        fromDate:
            todayDate,

        toDate:
            todayDate,

    },

    lastUpdated:
        null,
};


/*
|--------------------------------------------------------------------------
| FETCH DASHBOARD
|--------------------------------------------------------------------------
*/

export const fetchDashboardOverview =
    createAsyncThunk(

        "dashboard/fetchDashboardOverview",

        async (
            filters = {},
            {
                rejectWithValue,
            }
        ) => {

            try {

                const params = {

                    period:
                        filters?.period ||
                        "TODAY",

                    fromDate:
                        filters?.fromDate ||
                        "",

                    toDate:
                        filters?.toDate ||
                        "",

                };


                /*
                |--------------------------------------------------------------------------
                | BUILD QUERY STRING EXPLICITLY
                |--------------------------------------------------------------------------
                |
                | Building the final URL here makes the filter independent of
                | whichever apiCalling.js GET signature is loaded at runtime.
                |
                |--------------------------------------------------------------------------
                */

                const query =
                    new URLSearchParams();

                Object.entries(
                    params
                ).forEach(
                    (
                        [
                            key,
                            value,
                        ]
                    ) => {

                        if (
                            value ===
                                undefined ||
                            value ===
                                null ||
                            value ===
                                ""
                        ) {
                            return;
                        }

                        query.set(
                            key,
                            String(
                                value
                            )
                        );

                    }
                );

                const requestUrl =
                    query.toString()
                        ? `${config.dashboardOverview}?${query.toString()}`
                        : config.dashboardOverview;


                const response =
                    await api.get(
                        requestUrl
                    );


                /*
                |--------------------------------------------------------------------------
                | API FAILURE
                |--------------------------------------------------------------------------
                */

                if (
                    !response?.success
                ) {

                    return rejectWithValue(
                        response?.message ||
                        "Unable to load dashboard."
                    );
                }


                /*
                |--------------------------------------------------------------------------
                | BACKEND RESPONSE
                |--------------------------------------------------------------------------
                |
                | {
                |   success: true,
                |   data: {...}
                | }
                |
                |--------------------------------------------------------------------------
                */

                return (
                    response?.data ||
                    null
                );

            } catch (error) {

                console.error(
                    "Dashboard API Error:",
                    error
                );


                return rejectWithValue(
                    error?.message ||
                    "Unable to load dashboard."
                );
            }

        }
    );


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const dashboardSlice =
    createSlice({

        name:
            "dashboard",

        initialState,

        reducers: {


            /*
            |--------------------------------------------------------------------------
            | SET PERIOD
            |--------------------------------------------------------------------------
            */

            setDashboardPeriod: (
                state,
                action
            ) => {

                state.filters.period =
                    action.payload;
            },


            /*
            |--------------------------------------------------------------------------
            | SET FROM DATE
            |--------------------------------------------------------------------------
            */

            setDashboardFromDate: (
                state,
                action
            ) => {

                state.filters.fromDate =
                    action.payload;

                state.filters.period =
                    "CUSTOM";
            },


            /*
            |--------------------------------------------------------------------------
            | SET TO DATE
            |--------------------------------------------------------------------------
            */

            setDashboardToDate: (
                state,
                action
            ) => {

                state.filters.toDate =
                    action.payload;

                state.filters.period =
                    "CUSTOM";
            },


            /*
            |--------------------------------------------------------------------------
            | SET COMPLETE FILTER
            |--------------------------------------------------------------------------
            */

            setDashboardFilters: (
                state,
                action
            ) => {

                state.filters = {

                    ...state.filters,

                    ...action.payload,

                };
            },


            /*
            |--------------------------------------------------------------------------
            | RESET FILTER
            |--------------------------------------------------------------------------
            */

            resetDashboardFilters: (
                state
            ) => {

                const today =
                    getTodayDate();


                state.filters = {

                    period:
                        "TODAY",

                    fromDate:
                        today,

                    toDate:
                        today,

                };
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR DASHBOARD DATA
            |--------------------------------------------------------------------------
            */

            clearDashboardData: (
                state
            ) => {

                state.data =
                    null;

                state.error =
                    null;

                state.lastUpdated =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR ERROR
            |--------------------------------------------------------------------------
            */

            clearDashboardError: (
                state
            ) => {

                state.error =
                    null;
            },

        },


        /*
        |--------------------------------------------------------------------------
        | ASYNC
        |--------------------------------------------------------------------------
        */

        extraReducers: (
            builder
        ) => {

            builder


                /*
                |--------------------------------------------------------------------------
                | PENDING
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchDashboardOverview.pending,

                    (
                        state
                    ) => {

                        state.error =
                            null;


                        if (
                            state.data
                        ) {

                            state.refreshing =
                                true;

                        } else {

                            state.loading =
                                true;
                        }

                    }
                )


                /*
                |--------------------------------------------------------------------------
                | SUCCESS
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchDashboardOverview.fulfilled,

                    (
                        state,
                        action
                    ) => {

                        state.loading =
                            false;

                        state.refreshing =
                            false;

                        state.error =
                            null;

                        state.data =
                            action.payload;

                        state.lastUpdated =
                            new Date()
                                .toISOString();

                    }
                )


                /*
                |--------------------------------------------------------------------------
                | FAILED
                |--------------------------------------------------------------------------
                */

                .addCase(
                    fetchDashboardOverview.rejected,

                    (
                        state,
                        action
                    ) => {

                        state.loading =
                            false;

                        state.refreshing =
                            false;

                        state.error =
                            action.payload ||
                            "Unable to load dashboard.";

                    }
                );

        },

    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {

    setDashboardPeriod,

    setDashboardFromDate,

    setDashboardToDate,

    setDashboardFilters,

    resetDashboardFilters,

    clearDashboardData,

    clearDashboardError,

} = dashboardSlice.actions;


/*
|--------------------------------------------------------------------------
| MAIN SELECTORS
|--------------------------------------------------------------------------
*/

export const dashboardSelector =
    (
        state
    ) =>
        state.dashboard;


export const dashboardDataSelector =
    (
        state
    ) =>
        state.dashboard?.data;


export const dashboardFiltersSelector =
    (
        state
    ) =>
        state.dashboard?.filters;


export const dashboardLoadingSelector =
    (
        state
    ) =>
        state.dashboard?.loading;


export const dashboardRefreshingSelector =
    (
        state
    ) =>
        state.dashboard?.refreshing;


export const dashboardErrorSelector =
    (
        state
    ) =>
        state.dashboard?.error;


export const dashboardLastUpdatedSelector =
    (
        state
    ) =>
        state.dashboard?.lastUpdated;


/*
|--------------------------------------------------------------------------
| SECTION SELECTORS
|--------------------------------------------------------------------------
*/

export const dashboardPrioritySelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.priority ||
        {};


export const dashboardSalesSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.sales ||
        {};


export const dashboardWebsiteSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.website ||
        {};


export const dashboardTasksSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.tasks ||
        {};


export const dashboardEmployeesSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.employees ||
        {};


export const dashboardCustomersSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.customers ||
        {};


export const dashboardRequirementsSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.requirements ||
        {};


export const dashboardQueriesSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.queries ||
        {};


export const dashboardProductsSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.products ||
        {};


export const dashboardPurchasesSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.purchases ||
        {};


export const dashboardAlertsSelector =
    (
        state
    ) =>
        state.dashboard
            ?.data
            ?.alerts ||
        [];


export default dashboardSlice.reducer;